// Port of SuiteModel/MapInk.swift (the same rules and numbers): what a stroke the writer just drew on a map means — a
// line joining two notes, a ring selecting notes, a scribble scratching one out, or ink to keep — and which strokes lie
// close enough together to be read as one piece of handwriting.
import { MapLayout, type MapFrame, type MapFrames } from './layout.ts';
import { placedStroke, type MapContent, type MapPoint, type MapStroke, type MapStrokePoint } from './map.ts';

/** What a finished stroke does. */
export type MapInkGesture =
  /** Drawn from one note to another: join them. */
  | { kind: 'join'; a: string; b: string }
  /** A ring drawn round notes: select them. */
  | { kind: 'select'; ids: string[] }
  /** A scribble back and forth over a note: delete it. */
  | { kind: 'scratch'; id: string }
  /** Anything else: keep it as ink. */
  | { kind: 'ink' };

const maxX = (f: MapFrame) => f.x + f.width;
const maxY = (f: MapFrame) => f.y + f.height;

export const MapInk = {
  /** Points closer than this to the one kept before are dropped as the stroke is stored. */
  minimumStep: 1.5,
  /** A ring's ends must meet within this, or a quarter of its smaller side, whichever is larger. */
  closeEnough: 30,
  /** Strokes nearer each other than this are one piece of writing. */
  writingGap: 40,
  /** Turns back and forth that make a scribble. */
  scratchTurns: 4,

  /** The pen's points without the ones it barely moved between (the first and last always kept). */
  simplified(points: readonly MapStrokePoint[]): MapStrokePoint[] {
    if (points.length <= 2) return [...points];
    let last = points[0];
    const kept = [last];
    for (const p of points.slice(1, -1)) {
      if (Math.hypot(p.x - last.x, p.y - last.y) >= MapInk.minimumStep) {
        kept.push(p);
        last = p;
      }
    }
    kept.push(points[points.length - 1]);
    return kept;
  },

  /** What a stroke drawn over the map means. `frames` are where the notes are shown (stacked). */
  classify(points: readonly MapPoint[], content: MapContent, frames: MapFrames): MapInkGesture {
    if (points.length <= 1) return { kind: 'ink' };
    const first = points[0], last = points[points.length - 1];
    const inside = (p: MapPoint, f: MapFrame, margin: number) =>
      p.x >= f.x - margin && p.x <= maxX(f) + margin && p.y >= f.y - margin && p.y <= maxY(f) + margin;
    /** The note whose room holds a point (the last drawn, so the one on top). */
    const noteAt = (p: MapPoint): string | null => {
      for (let i = content.notes.length - 1; i >= 0; i--) {
        const room = MapLayout.occupied(content.notes[i].id, content, frames);
        if (room && inside(p, room, 4)) return content.notes[i].id;
      }
      return null;
    };

    // A scribble: most of it over one note, turning back and forth along x.
    const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
    const x0 = Math.min(...xs), y0 = Math.min(...ys);
    const box: MapFrame = { x: x0, y: y0, width: Math.max(...xs) - x0, height: Math.max(...ys) - y0 };
    let turns = 0, heading = 0, travelled = 0;
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      if (dx === 0) continue;
      if (heading !== 0 && (dx > 0) !== (heading > 0)) {
        if (Math.abs(travelled) > 4) turns += 1;
        travelled = 0;
      }
      heading = dx;
      travelled += dx;
    }
    if (turns >= MapInk.scratchTurns) {
      for (let i = content.notes.length - 1; i >= 0; i--) {
        const f = frames.get(content.notes[i].id);
        if (!f) continue;
        const over = points.filter((p) => inside(p, f, 6)).length;
        if (over >= points.length * 0.6) return { kind: 'scratch', id: content.notes[i].id };
      }
    }

    // From one note to another.
    const a = noteAt(first), b = noteAt(last);
    if (a !== null && b !== null && a !== b) return { kind: 'join', a, b };

    // A ring: long enough, ends meeting, round some notes.
    let length = 0;
    for (let i = 1; i < points.length; i++) length += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    const gap = Math.hypot(last.x - first.x, last.y - first.y);
    if (length > 60 && box.width >= 30 && box.height >= 30 && gap <= Math.max(MapInk.closeEnough, Math.min(box.width, box.height) / 4)) {
      const ringed = content.notes.flatMap((n) => {
        const f = frames.get(n.id);
        return f && MapInk.contains(points, { x: f.x + f.width / 2, y: f.y + f.height / 2 }) ? [n.id] : [];
      });
      if (ringed.length) return { kind: 'select', ids: ringed };
    }
    return { kind: 'ink' };
  },

  /** Whether a closed path (the last point joined back to the first) holds a point (even-odd). */
  contains(path: readonly MapPoint[], p: MapPoint): boolean {
    let inside = false;
    let j = path.length - 1;
    for (let i = 0; i < path.length; i++) {
      const a = path[i], b = path[j];
      if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
      j = i;
    }
    return inside;
  },

  /** The frame round strokes where they are now. */
  bounds(strokes: readonly MapStroke[]): MapFrame | null {
    const points = strokes.flatMap(placedStroke);
    if (!points.length) return null;
    const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
    const x = Math.min(...xs), y = Math.min(...ys);
    return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
  },

  /** Strokes in groups that lie within `writingGap` of each other (their frames, grown by half of it, touching), each
   *  group in the order its strokes are given, groups by their first stroke. */
  clusters(strokes: readonly MapStroke[]): string[][] {
    const frames = strokes.map((s) => MapInk.bounds([s]));
    const group = strokes.map((_, i) => i);
    const root = (i: number): number => (group[i] === i ? i : root(group[i]));
    const half = MapInk.writingGap / 2;
    for (let i = 0; i < strokes.length; i++) {
      for (let j = i + 1; j < strokes.length; j++) {
        const a = frames[i], b = frames[j];
        if (!a || !b) continue;
        if (a.x - half < maxX(b) + half && b.x - half < maxX(a) + half && a.y - half < maxY(b) + half && b.y - half < maxY(a) + half) {
          const ri = root(i), rj = root(j);
          if (ri !== rj) group[Math.max(ri, rj)] = Math.min(ri, rj);
        }
      }
    }
    const order: number[] = [];
    const members = new Map<number, string[]>();
    strokes.forEach((s, i) => {
      const r = root(i);
      if (!members.has(r)) {
        order.push(r);
        members.set(r, []);
      }
      members.get(r)!.push(s.id);
    });
    return order.map((r) => members.get(r) ?? []);
  },

  /** `stroke(at:in:reach:)`: the stroke nearest a point within `reach` of its line (the last drawn wins a tie), for
   *  choosing one to move or delete. */
  strokeAt(p: MapPoint, strokes: readonly MapStroke[], reach: number): string | null {
    let best: [string, number] | null = null;
    for (const stroke of strokes) {
      const placed = placedStroke(stroke);
      const pairs: [MapPoint, MapPoint][] = placed.length === 1 ? [[placed[0], placed[0]]] : placed.slice(1).map((b, i) => [placed[i], b]);
      for (const [a, b] of pairs) {
        const dx = b.x - a.x, dy = b.y - a.y;
        const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / Math.max(dx * dx + dy * dy, 0.0001)));
        const d = Math.hypot(a.x + dx * t - p.x, a.y + dy * t - p.y);
        if (d <= reach + stroke.width / 2 && d <= (best?.[1] ?? Infinity)) best = [stroke.id, d];
      }
    }
    return best?.[0] ?? null;
  },
};
